import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, yieldToMainThread, prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @param {ParentNode | null | undefined} root
 * @returns {boolean}
 */
export function hasCompleteSizeSelection(root) {
  const fieldsets = (root ?? document).querySelectorAll('fieldset.variant-option--grid');
  if (fieldsets.length === 0) return true;

  for (const fieldset of fieldsets) {
    if (!fieldset.querySelector('input[type="radio"]:checked')) return false;
  }

  return true;
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {ParentNode}
 */
function sizeRequiredMessageScope(root) {
  if (root instanceof Element) {
    return root.closest('.shopify-section') ?? root;
  }

  return root ?? document;
}

/**
 * @param {ParentNode | null | undefined} root
 */
export function hideSizeRequiredMessage(root) {
  for (const message of sizeRequiredMessageScope(root).querySelectorAll('[data-size-required-message]')) {
    if (!(message instanceof HTMLElement)) continue;
    message.classList.remove('is-visible');
    message.setAttribute('aria-hidden', 'true');
  }
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {HTMLElement | null}
 */
export function showSizeRequiredMessage(root) {
  const scope = sizeRequiredMessageScope(root);
  hideSizeRequiredMessage(scope);

  // Each size picker owns its notice: show "select a size" under every
  // still-unselected size fieldset, and leave already-picked ones clean. For a
  // gear set only the missing component (e.g. the Jersey) is flagged; a regular
  // product flags its single size picker.
  const fieldsets = Array.from(scope.querySelectorAll('fieldset.variant-option--grid')).filter(
    (fieldset) => fieldset instanceof HTMLElement,
  );

  let firstUnselected = null;
  let shownAny = false;
  for (const fieldset of fieldsets) {
    if (fieldset.querySelector('input[type="radio"]:checked')) continue;
    firstUnselected ??= fieldset;

    const fieldsetMessage = fieldset.querySelector('[data-size-required-fieldset]');
    if (fieldsetMessage instanceof HTMLElement) {
      fieldsetMessage.classList.add('is-visible');
      fieldsetMessage.setAttribute('aria-hidden', 'false');
      shownAny = true;
    }
  }

  // Fallback to the single global line only if no per-picker notice exists
  // (e.g. a template that renders the size grid without the inline element).
  if (!shownAny && firstUnselected) {
    const globalMessage =
      scope.querySelector('.product-form-buttons [data-size-required-message]') ??
      scope.querySelector('[data-size-required-message]:not([data-size-required-fieldset])');
    if (globalMessage instanceof HTMLElement) {
      globalMessage.classList.add('is-visible');
      globalMessage.setAttribute('aria-hidden', 'false');
    }
  }

  return firstUnselected;
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {boolean}
 */
export function scrollToSizeVariantPicker(root) {
  const scope = root instanceof Element ? root : document;
  const picker = scope.querySelector('variant-picker');
  if (!(picker instanceof HTMLElement)) return false;

  picker.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });

  if (!picker.hasAttribute('tabindex')) {
    picker.setAttribute('tabindex', '-1');
  }
  picker.focus({ preventScroll: true });

  return true;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));

    if (productForm?.dataset.requiresSizeSelection === 'true') {
      const section = productForm.closest('.shopify-section');
      const productId = productForm.dataset.productId;
      const picker =
        section && productId
          ? section.querySelector(`variant-picker[data-product-id="${productId}"]`)
          : section?.querySelector('variant-picker');

      if (!hasCompleteSizeSelection(picker)) {
        showSizeRequiredMessage(productForm);
        return;
      }
    }

    // Check if adding would exceed max before animating
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      this.animateAddToCart();
    }

    // Size-required products render a non-submitting button (type="button") so a
    // click before product-form.js upgrades can't natively post an empty variant
    // id (422). Now that the guards above have passed, drive the add through the
    // form's submit handler explicitly. Native submit buttons still submit on
    // their own, so we must not double-submit them here.
    if (this.refs.addToCartButton.type !== 'submit') {
      this.closest('form')?.requestSubmit();
    }
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Guards async buy-button morphs so a stale fetch cannot overwrite a newer size. */
  #variantUpdateGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Keep variant id empty until a size is picked, unless ?variant= restored one.
    queueMicrotask(() => this.#syncVariantIdForSizeSelection(null));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled,
    );
    if (anyButtonDisabled) return;

    if (this.dataset.requiresSizeSelection === 'true') {
      const picker = this.#getSectionVariantPicker();
      if (!hasCompleteSizeSelection(picker)) {
        showSizeRequiredMessage(this);
        this.#setLiveRegionText(
          this.querySelector('[data-size-required-message]')?.textContent?.trim() ||
            picker?.querySelector('[data-size-required-message]')?.textContent?.trim() ||
            '',
        );
        return;
      }

      hideSizeRequiredMessage(this);

      // Gear-set (and single-size) variants resolve to an id via an async section
      // round-trip; the hidden id stays empty until it lands (see
      // #onVariantSelected, which intentionally defers gear-set ids). A fast add
      // right after selecting can fire in that gap and post an empty id — Shopify
      // returns a 422 "Cannot find variant". Wait for the resolved id first.
      if (!this.refs.variantId.value) {
        const resolved = await this.#waitForResolvedVariantId();
        // Resolution never landed (e.g. the section fetch failed) — don't submit
        // an empty id. Surface the size prompt so the shopper can retry.
        if (!resolved) {
          showSizeRequiredMessage(this);
          return;
        }
      }
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Check if product-variations component exists and has selections
    // The product-variations component might be outside the form (e.g., in a drawer)
    // So we need to search in the parent section/context, not just within the form
    const parentSection = this.closest('.shopify-section, dialog, product-card, section') || document;

    let productVariations = /** @type {any | null} */ (
      parentSection.querySelector('product-variations[data-selection-mode="checkbox"]')
    );

    if (!productVariations) {
      const allVariations = parentSection.querySelectorAll('product-variations');
      for (const variation of allVariations) {
        /** @type {any} */
        const variationComponent = variation;
        if (variationComponent.hasSelectedVariations?.()) {
          productVariations = variationComponent;
          break;
        }
      }
    }

    if (productVariations?.hasSelectedVariations?.()) {
      // Handle product variations checkbox mode
      await this.#handleProductVariationsSubmit(event, productVariations, allAddToCartContainers);
      return;
    }

    const formData = new FormData(form);

    // Attach the Sizebay v4 session ID (cookie) as a cart attribute so the
    // order can be attributed to a Sizebay recommendation. No-op when
    // Sizebay isn't enabled / the user hasn't interacted with the widget.
    if (typeof (/** @type {any} */ (window).getSizebaySessionIdV4) === 'function') {
      const sizebaySessionId = /** @type {any} */ (window).getSizebaySessionIdV4();
      if (sizebaySessionId) formData.append('attributes[SIZEBAY_SESSION_ID_V4]', sizebaySessionId);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Dispatch the cart add event immediately so the cart drawer opens as
          // early as possible. Blocking on the extra `/cart.js` round-trip below
          // used to delay the overlay long enough that the "Add to cart" button
          // finished its animation and reappeared before the drawer covered it.
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );

          // Sync this variant's quantity selector in parallel — the drawer has
          // already been told to open, so this no longer gates the overlay.
          this.#fetchAndUpdateCartQuantity();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Handles submit when product variations are selected (checkbox mode)
   * @param {Event} event - The submit event
   * @param {any} productVariations - The product-variations component
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers - All add-to-cart button containers
   */
  async #handleProductVariationsSubmit(event, productVariations, allAddToCartContainers) {
    const { addToCartTextError } = this.refs;

    // Disable ALL add-to-cart buttons while processing
    for (const container of allAddToCartContainers) {
      container.disable();
    }

    try {
      const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
      const currentVariantId = variantIdInput?.value || null;
      const result = await productVariations.addSelectedToCart(currentVariantId);

      if (result.success) {
        for (const container of allAddToCartContainers) {
          container.animateAddToCart();
        }

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        const drawer = productVariations.closest('product-variations-drawer');
        if (drawer) {
          const closeButton = drawer.querySelector('[ref="closeButton"]');
          if (closeButton instanceof HTMLElement) {
            closeButton.click();
          }
        }
      } else {
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = result.error || 'Error adding items to cart';
          } else {
            const newTextNode = document.createTextNode(result.error || 'Error adding items to cart');
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(result.error || 'Error adding items to cart');

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }
      }
    } catch (error) {
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const errorMsg = 'Error adding items to cart';

        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMsg;
        } else {
          const newTextNode = document.createTextNode(errorMsg);
          addToCartTextError.appendChild(newTextNode);
        }

        this.#setLiveRegionText(errorMsg);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }
    } finally {
      setTimeout(() => {
        for (const container of allAddToCartContainers) {
          container.enable();
        }
      }, ERROR_BUTTON_REENABLE_DELAY);

      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Finds the `product-form-component` in fetched HTML that matches this instance.
   * Unscoped `querySelector` on the full response can grab another product's form when
   * multiple forms exist (e.g. recommendations), so add-to-cart / notify-me never updates.
   * @param {ParentNode} html
   * @returns {HTMLElement | null}
   */
  #getResponseProductFormRoot(html) {
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;
    const candidates = /** @type {NodeListOf<HTMLElement>} */ (html.querySelectorAll('product-form-component'));

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0] ?? null;

    for (const element of candidates) {
      if (
        sectionId &&
        element.dataset.sectionId === sectionId &&
        productId &&
        element.dataset.productId === productId
      ) {
        return element;
      }
    }

    for (const element of candidates) {
      if (sectionId && element.dataset.sectionId === sectionId) {
        return element;
      }
    }

    for (const element of candidates) {
      if (productId && element.dataset.productId === productId) {
        return element;
      }
    }

    return candidates[0] ?? null;
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSectionVariantPicker() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    if (!section || !productId) return null;

    const picker = section.querySelector(`variant-picker[data-product-id="${productId}"]`);
    return picker instanceof HTMLElement ? picker : null;
  }

  /**
   * Waits for the async section round-trip to populate the hidden variant id.
   * Gear-set / size selections resolve to a variant server-side, so the id is
   * briefly empty after the shopper picks; adding in that window would post an
   * empty id (422). Polls until an id is present or the timeout elapses.
   * @param {number} [timeoutMs]
   * @returns {Promise<boolean>} true once an id is present, false on timeout
   */
  #waitForResolvedVariantId(timeoutMs = 4000) {
    const { variantId } = this.refs;
    if (variantId.value) return Promise.resolve(true);

    return new Promise((resolve) => {
      const start = performance.now();
      const check = () => {
        if (variantId.value) {
          resolve(true);
        } else if (performance.now() - start >= timeoutMs) {
          resolve(false);
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });
  }

  /**
   * Size gate + notify morph. Variant id itself comes from Horizon-style
   * resource updates in #onVariantUpdate.
   * @param {{ id?: string | number, available?: boolean } | null | undefined} resource
   * @param {number} [generation]
   */
  async #syncVariantIdForSizeSelection(resource, generation = this.#variantUpdateGeneration) {
    if (this.dataset.requiresSizeSelection !== 'true') return;
    if (generation !== this.#variantUpdateGeneration) return;

    const { variantId, acceleratedCheckoutButtonContainer } = this.refs;
    const picker = this.#getSectionVariantPicker();

    if (!hasCompleteSizeSelection(picker)) {
      variantId.value = '';
      hideSizeRequiredMessage(picker);
      acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    if (resource?.id != null) {
      variantId.value = String(resource.id);
    }

    const checkedSize = picker?.querySelector('fieldset.variant-option--grid input[type="radio"]:checked');
    const sizeUnavailable =
      checkedSize instanceof HTMLInputElement &&
      (checkedSize.dataset.optionAvailable === 'false' ||
        checkedSize.getAttribute('aria-disabled') === 'true' ||
        checkedSize.closest('label')?.classList.contains('is-unavailable') === true);
    const resourceUnavailable = resource != null && resource.available === false;

    if (sizeUnavailable || resourceUnavailable) {
      if (variantId.value) {
        await this.#morphBuyButtonsForVariant(variantId.value, generation);
      }
      if (generation !== this.#variantUpdateGeneration) return;
      acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    if (!variantId.value) return;

    if (!this.querySelector('add-to-cart-component')) {
      await this.#morphBuyButtonsForVariant(variantId.value, generation);
      if (generation !== this.#variantUpdateGeneration) return;
    }

    hideSizeRequiredMessage(picker);
    acceleratedCheckoutButtonContainer?.removeAttribute('hidden');

    const { addToCartButtonContainer } = this.refs;
    const button = addToCartButtonContainer?.refs.addToCartButton;
    const addToCartText = this.dataset.addToCartText;
    if (addToCartButtonContainer && button instanceof HTMLButtonElement) {
      addToCartButtonContainer.enable();
      const textEl = button.querySelector('.add-to-cart-text__content');
      if (addToCartText && textEl instanceof HTMLElement) {
        textEl.textContent = addToCartText;
      }
    }
  }

  /**
   * Fetches buy-button HTML for an explicit variant so notify/sold-out can render.
   * @param {string | number} variantId
   * @param {number} [generation]
   */
  async #morphBuyButtonsForVariant(variantId, generation = this.#variantUpdateGeneration) {
    if (generation !== this.#variantUpdateGeneration) return;

    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    if (!productUrl || !sectionId) return;

    const url = `${productUrl}?variant=${encodeURIComponent(String(variantId))}&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url);
      const responseText = await response.text();
      if (generation !== this.#variantUpdateGeneration) return;

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const responseRoot = this.#getResponseProductFormRoot(html);
      const currentAddToCartWrapper = this.querySelector('[ref="addToCartWrapper"]');
      const newAddToCartWrapper = responseRoot?.querySelector('[ref="addToCartWrapper"]');

      if (currentAddToCartWrapper && newAddToCartWrapper) {
        morph(currentAddToCartWrapper, newAddToCartWrapper);
      }

      // Re-apply only for this generation — never let a stale morph win.
      if (generation === this.#variantUpdateGeneration) {
        const { variantId: variantIdInput } = this.refs;
        variantIdInput.value = String(variantId);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    const generation = ++this.#variantUpdateGeneration;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    const responseRoot = this.#getResponseProductFormRoot(event.detail.data.html);

    // Horizon default: form id tracks the section-resolved variant.
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    // First, try to morph the entire add-to-cart wrapper (handles switching between add-to-cart and notify-me)
    const currentAddToCartWrapper = this.querySelector('[ref="addToCartWrapper"]');
    const newAddToCartWrapper = responseRoot?.querySelector('[ref="addToCartWrapper"]');

    if (currentAddToCartWrapper && newAddToCartWrapper) {
      morph(currentAddToCartWrapper, newAddToCartWrapper);
      // Morph can restore a stale hidden id from section HTML — re-apply resolved id.
      variantId.value = event.detail.resource?.id ?? '';
    } else if (currentAddToCartButtonContainer && currentAddToCartButton) {
      // Fallback to old behavior if wrapper not found
      if (event.detail.resource == null || event.detail.resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = responseRoot?.querySelector('[ref="addToCartButton"]');
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (generation !== this.#variantUpdateGeneration) return;

    if (this.dataset.requiresSizeSelection !== 'true' && acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      responseRoot?.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = responseRoot?.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = responseRoot?.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = responseRoot?.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          responseRoot?.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }

        // product-form-buttons morph can overwrite the hidden id — restore resolved.
        variantId.value = event.detail.resource?.id ?? '';
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, responseRoot?.querySelector(selector), fallback);
      }
    }

    if (generation !== this.#variantUpdateGeneration) return;

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = responseRoot?.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    // Size gate last — earlier morphs of product-form-buttons can overwrite Notify me.
    await this.#syncVariantIdForSizeSelection(event.detail.resource, generation);
    if (generation !== this.#variantUpdateGeneration) return;

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Optimistic id for single-size products so ATC is not waiting on section fetch.
   * Gear sets clear the id until option_values resolve (a single radio id is only
   * a partial), so a fast add waits for the real bundle id instead of the stale one.
   */
  #onVariantSelected = () => {
    if (this.dataset.requiresSizeSelection !== 'true') return;

    const picker = this.#getSectionVariantPicker();
    if (!hasCompleteSizeSelection(picker)) {
      this.refs.variantId.value = '';
      return;
    }

    const sizeGridCount = picker?.querySelectorAll('fieldset.variant-option--grid').length ?? 0;
    if (sizeGridCount > 1) {
      // Gear set: a single radio's id is only a partial (one component), so we
      // can't set the real bundle id optimistically. Clear the previous id so a
      // fast re-add waits for the async resolution (see #waitForResolvedVariantId)
      // instead of submitting the now-stale variant.
      this.refs.variantId.value = '';
      return;
    }

    const checkedSize = picker?.querySelector('fieldset.variant-option--grid input[type="radio"]:checked');
    if (checkedSize instanceof HTMLInputElement && checkedSize.dataset.variantId) {
      this.refs.variantId.value = checkedSize.dataset.variantId;
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
